import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard,
  Briefcase,
  Users,
  KanbanSquare,
  Star,
  MessageSquare,
  Inbox,
  Mail,
  FileText,
  HelpCircle,
  Search,
  Settings,
  LogOut,
  Menu,
  X,
  Building2,
  UserCog,
  Calendar,
  Award,
  Receipt,
  Send,
  Share2,
  BarChart3,
  KeyRound,
  Handshake,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, getToken, setToken } from "@/lib/api";
import { useSiteSettings } from "@/lib/settings";

const NAV = [
  { section: "Overview", items: [
    { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/admin/kpi", label: "Business KPIs", icon: BarChart3 },
  ]},
  { section: "Talent", items: [
    { to: "/admin/applications", label: "Applications", icon: Users },
    { to: "/admin/pipeline", label: "Pipeline", icon: KanbanSquare },
    { to: "/admin/talent-search", label: "Talent Search", icon: Search },
    { to: "/admin/interviews", label: "Interviews", icon: Calendar },
  ]},
  { section: "Placement Ops", items: [
    { to: "/admin/clients", label: "Clients", icon: Building2 },
    { to: "/admin/jobs", label: "Jobs / Mandates", icon: Briefcase },
    { to: "/admin/shortlists", label: "Client Shortlists", icon: Share2 },
    { to: "/admin/placements", label: "Placements", icon: Award },
    { to: "/admin/invoices", label: "Invoices", icon: Receipt },
  ]},
  { section: "Team & Comms", items: [
    { to: "/admin/recruiters", label: "Recruiters", icon: UserCog },
    { to: "/admin/templates", label: "Message Templates", icon: Send },
    { to: "/admin/enquiries", label: "Enquiries", icon: Inbox },
    { to: "/admin/hire-briefs", label: "Hire Briefs", icon: Handshake },
    { to: "/admin/subscribers", label: "Subscribers", icon: Mail },
  ]},
  { section: "Content & SEO", items: [
    { to: "/admin/testimonials", label: "Testimonials", icon: Star },
    { to: "/admin/feedback", label: "Feedback", icon: MessageSquare },
    { to: "/admin/blog", label: "Blog", icon: FileText },
    { to: "/admin/faqs", label: "FAQs", icon: HelpCircle },
    { to: "/admin/departments", label: "Departments", icon: Building2 },
    { to: "/admin/service-categories", label: "Service Categories", icon: Briefcase },
    { to: "/admin/page-seo", label: "Page SEO", icon: Search },
    { to: "/admin/settings", label: "Settings", icon: Settings },
    { to: "/admin/change-password", label: "Change Password", icon: KeyRound },
  ]},
];

// Which nav items get an unread badge (key must match /admin/notifications/unread response)
const BADGE_KEYS = {
  "/admin/applications": "applications_new",
  "/admin/enquiries": "enquiries",
  "/admin/hire-briefs": "hire_briefs",
  "/admin/subscribers": "subscribers",
  "/admin/feedback": "feedback",
  "/admin/testimonials": "testimonials_pending",
};

export default function AdminLayout() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState({});
  const { settings } = useSiteSettings();

  useEffect(() => {
    if (!getToken()) {
      nav("/admin/login", { replace: true });
      return;
    }
    api.get("/admin/auth/me").then((r) => setMe(r.data)).catch(() => {
      setToken(null);
      nav("/admin/login", { replace: true });
    });
  }, [nav]);

  useEffect(() => {
    if (!me) return;
    const load = () => api.get("/admin/notifications/unread").then((r) => setUnread(r.data || {})).catch(() => {});
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [me]);

  const logout = () => {
    setToken(null);
    nav("/admin/login", { replace: true });
  };

  if (!me) return <div className="flex min-h-screen items-center justify-center bg-slate-50 text-slate-500">Loading…</div>;

  return (
    <div className="flex min-h-screen bg-slate-50">
      <aside className={`fixed inset-y-0 left-0 z-30 w-64 transform border-r border-slate-200 bg-white transition-transform lg:static lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`} data-testid="admin-sidebar">
        <div className="flex h-20 items-center justify-between border-b border-slate-200 px-4">
          <Link to="/admin/dashboard" className="flex items-center gap-2.5">
            <span className="logo-mark inline-flex h-11 w-11 items-center justify-center">
              <img src={settings.logo_url} alt="Brand logo" className="h-full w-full rounded-md object-cover" />
            </span>
            <div className="font-heading text-base font-bold leading-tight">
              <span className="text-primary">{settings.brand_left}</span><span className="text-secondary">{settings.brand_right}</span>
              <div className="text-[9px] uppercase tracking-widest text-slate-400">Admin Console</div>
            </div>
          </Link>
          <button className="rounded p-1 text-slate-500 hover:bg-slate-100 lg:hidden" onClick={() => setOpen(false)}><X className="h-5 w-5" /></button>
        </div>
        <nav className="flex flex-col gap-0.5 overflow-y-auto p-3 pb-24 text-sm">
          {NAV.map(({ section, items }) => (
            <div key={section} className="mt-3 first:mt-0">
              <div className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-widest text-slate-400">{section}</div>
              {items.map(({ to, label, icon: Icon }) => {
                const badgeKey = BADGE_KEYS[to];
                const count = badgeKey ? unread[badgeKey] || 0 : 0;
                return (
                  <NavLink
                    key={to}
                    to={to}
                    onClick={() => setOpen(false)}
                    data-testid={`adminnav-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 rounded-md px-3 py-2 transition-colors ${isActive ? "bg-primary/10 font-semibold text-primary" : "text-slate-700 hover:bg-slate-100"}`
                    }
                  >
                    <Icon className="h-4 w-4" />
                    <span className="flex-1">{label}</span>
                    {count > 0 && (
                      <span
                        className="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-secondary px-1.5 py-0.5 text-[10px] font-bold text-secondary-foreground"
                        data-testid={`badge-${to.split('/').pop()}`}
                      >
                        {count > 99 ? "99+" : count}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>
      </aside>

      <div className="flex flex-1 flex-col lg:pl-0">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-slate-200 bg-white px-4 lg:px-6">
          <button className="rounded p-1 text-slate-700 hover:bg-slate-100 lg:hidden" onClick={() => setOpen(true)} data-testid="admin-menu-open">
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-3 ml-auto">
            <div className="hidden text-right text-sm sm:block">
              <div className="font-semibold text-slate-900" data-testid="admin-user-name">{me.name}</div>
              <div className="text-xs text-slate-500">{me.email}</div>
            </div>
            <Button variant="outline" size="sm" onClick={logout} data-testid="admin-logout" className="gap-1.5">
              <LogOut className="h-3.5 w-3.5" /> Logout
            </Button>
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
